import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlarmClock,
  CalendarDays,
  CalendarX,
  CheckCircle2,
  Flag,
  LayoutGrid,
  List,
  Pencil,
  Play,
  Swords,
  Zap,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  buildNameMap,
  buildScheduleTimeline,
  collectMemberIds,
  collectPlayerIds,
  competitionKeys,
  courtAccent,
  formatDate,
  matchKeys,
  MatchStatusMark,
  roundAccent,
  roundLabel,
  runMatchAction,
  ScheduleGrid,
  scoreSummary,
  shortTime,
  toMinutes,
  toViewStatus,
  useClubCourtsQuery,
  useCompetitionMatchesQuery,
  useCompetitionQuery,
  useCourtsQuery,
  useMatchPlannerQuery,
  useParticipantsQuery,
  type ApiMatch,
  type ApiMatchStatus,
  type ScheduleCourt,
  type ScheduleTimeline,
} from '@/features/competition';
import { getUsersByIds, userKeys } from '@/features/user';
import { ApiError } from '@/shared/api/ApiError';
import { cn } from '@/shared/lib/cn';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  DateInput,
  EmptyState,
  IconButton,
  SegmentedControl,
  Select,
  Skeleton,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
  useToast,
} from '@/shared/ui';
import { ScheduleMatchDialog } from './scheduled/ScheduleMatchDialog';

/** Ton etykiety statusu meczu — spójny ze stroną wyniku. */
const STATUS_TONE: Record<
  ApiMatchStatus,
  'brand' | 'success' | 'info' | 'warning' | 'danger' | 'neutral'
> = {
  SCHEDULED: 'info',
  IN_PROGRESS: 'brand',
  SUSPENDED: 'warning',
  FINISHED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
  WALKOVER: 'warning',
};

/** Filtr statusu — `ALL` pokazuje wszystko. */
type StatusFilter = 'ALL' | ApiMatchStatus;

const STATUS_FILTERS: readonly StatusFilter[] = [
  'ALL',
  'SCHEDULED',
  'IN_PROGRESS',
  'SUSPENDED',
  'FINISHED',
  'COMPLETED',
  'WALKOVER',
  'CANCELLED',
];

/** Zakres czasowy filtra terminu. */
type RangeFilter = 'all' | 'today' | 'tomorrow' | 'dayAfter' | 'nextWeek';

/** Widok zakladki: lista albo os czasu kortow. */
type ViewMode = 'list' | 'grid';

/** Data lokalna w formacie ISO (yyyy-MM-dd). */
function localIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Zakładka „Mecze" — wszystkie mecze turnieju w jednej tabeli: numer, faza,
 * strony, wynik, status, kort i termin. Nad tabelą kafelki statystyk i filtry
 * (status, kort, zakres terminu), w wierszu szybka edycja terminu, a przełącznik
 * widoku pokazuje oś czasu kortów klubu. Klik w wiersz otwiera ekran wyniku.
 */
export function TournamentMatchesPage() {
  const { t } = useTranslation('tournament');
  const { t: tc } = useTranslation('common');
  const { competitionId = '' } = useParams<{ competitionId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const nowDate = new Date();
  const todayIso = localIsoDate(nowDate);
  const tomorrowIso = localIsoDate(new Date(nowDate.getTime() + 86_400_000));
  const dayAfterIso = localIsoDate(new Date(nowDate.getTime() + 2 * 86_400_000));
  // Następny tydzień kalendarzowy: poniedziałek–niedziela po bieżącym tygodniu.
  const daysSinceMonday = (nowDate.getDay() + 6) % 7;
  const nextWeekStartIso = localIsoDate(
    new Date(nowDate.getTime() + (7 - daysSinceMonday) * 86_400_000),
  );
  const nextWeekEndIso = localIsoDate(
    new Date(nowDate.getTime() + (13 - daysSinceMonday) * 86_400_000),
  );
  const nowMinutes = nowDate.getHours() * 60 + nowDate.getMinutes();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [courtFilter, setCourtFilter] = useState('');
  const [range, setRange] = useState<RangeFilter>('all');
  const [view, setView] = useState<ViewMode>('list');
  const [plannerDate, setPlannerDate] = useState(todayIso);
  const [editing, setEditing] = useState<ApiMatch | null>(null);

  const competitionQuery = useCompetitionQuery(competitionId);
  const matchesQuery = useCompetitionMatchesQuery(competitionId);
  const participantsQuery = useParticipantsQuery(competitionId);
  const courtsQuery = useCourtsQuery(competitionId);

  // Korty klubu — do filtra, dialogu planowania i osi czasu (sloty meczow
  // wskazuja korty klubu-organizatora).
  const clubId = competitionQuery.data?.clubId ?? undefined;
  const clubCourtsQuery = useClubCourtsQuery(clubId);
  // Os czasu pobiera planner tylko wtedy, gdy widok jest aktywny.
  const plannerQuery = useMatchPlannerQuery(view === 'grid' ? clubId : undefined, plannerDate);
  const planner = plannerQuery.data;

  // Szybkie akcje z listy — start i koniec gry bez wchodzenia w mecz.
  const actionMutation = useMutation({
    mutationFn: ({ matchId, action }: { matchId: string; action: 'start' | 'finish' }) =>
      runMatchAction(matchId, action),
    onSuccess: (_data, { matchId, action }) => {
      void queryClient.invalidateQueries({ queryKey: competitionKeys.matches(competitionId) });
      void queryClient.invalidateQueries({ queryKey: matchKeys.detail(matchId) });
      showToast({
        tone: 'success',
        description:
          action === 'start' ? t('matchResult.toast.started') : t('matchResult.toast.finished'),
      });
    },
    onError: (error) =>
      showToast({
        tone: 'danger',
        description:
          error instanceof ApiError ? error.message : t('matchResult.toast.actionFailed'),
      }),
  });

  const matches = useMemo(() => matchesQuery.data ?? [], [matchesQuery.data]);
  const participants = useMemo(() => participantsQuery.data ?? [], [participantsQuery.data]);

  // Jeden batch nazwisk dla tabeli i osi czasu (planner moze niesc graczy
  // z innych turniejow klubu).
  const plannerMatches = useMemo(
    () => (planner?.scheduledMatches ?? []).map((entry) => entry.match),
    [planner],
  );
  const memberIds = useMemo(
    () =>
      [...new Set([...collectMemberIds(participants), ...collectPlayerIds(plannerMatches)])].sort(),
    [participants, plannerMatches],
  );
  const profilesQuery = useQuery({
    queryKey: [...userKeys.all, 'batch', memberIds.join(',')],
    queryFn: () => getUsersByIds(memberIds),
    enabled: memberIds.length > 0,
  });
  const names = useMemo(() => buildNameMap(profilesQuery.data ?? []), [profilesQuery.data]);
  const courts = useMemo(
    () => new Map((courtsQuery.data ?? []).map((court) => [court.id, court])),
    [courtsQuery.data],
  );

  const sorted = useMemo(
    () => matches.slice().sort((a, b) => (a.matchNumber ?? 0) - (b.matchNumber ?? 0)),
    [matches],
  );
  const visible = useMemo(
    () =>
      sorted.filter((match) => {
        if (statusFilter !== 'ALL' && match.status !== statusFilter) return false;
        if (courtFilter && match.courtId !== courtFilter) return false;
        if (range === 'today' && match.scheduledDate !== todayIso) return false;
        if (range === 'tomorrow' && match.scheduledDate !== tomorrowIso) return false;
        if (range === 'dayAfter' && match.scheduledDate !== dayAfterIso) return false;
        if (
          range === 'nextWeek' &&
          (!match.scheduledDate ||
            match.scheduledDate < nextWeekStartIso ||
            match.scheduledDate > nextWeekEndIso)
        ) {
          return false;
        }
        return true;
      }),
    [
      sorted,
      statusFilter,
      courtFilter,
      range,
      todayIso,
      tomorrowIso,
      dayAfterIso,
      nextWeekStartIso,
      nextWeekEndIso,
    ],
  );

  // Kafelki statystyk — z pelnej listy, niezaleznie od filtrow.
  const scheduledCount = useMemo(
    () => matches.filter((match) => match.status === 'SCHEDULED').length,
    [matches],
  );
  const liveCount = useMemo(
    () =>
      matches.filter((match) => match.status === 'IN_PROGRESS' || match.status === 'SUSPENDED')
        .length,
    [matches],
  );
  const playedCount = useMemo(
    () =>
      matches.filter(
        (match) =>
          match.status === 'COMPLETED' ||
          match.status === 'WALKOVER' ||
          match.status === 'FINISHED',
      ).length,
    [matches],
  );
  const unscheduledCount = useMemo(
    () => matches.filter((match) => match.status === 'SCHEDULED' && !match.scheduledDate).length,
    [matches],
  );

  /** Nazwiska jednej strony jako tekst — do osi czasu i opisu dialogu. */
  function sideText(ids: readonly string[]): string {
    if (ids.length === 0) return '—';
    return ids.map((id) => names.get(id) ?? '…').join(' / ');
  }

  /** Korty do naglowka osi czasu. */
  const gridCourts: ScheduleCourt[] = useMemo(
    () =>
      (planner?.courts ?? []).map((court) => ({
        id: court.id,
        name: court.name || tc('scheduledMatchesPage.court', { number: court.number }),
        ...(court.surface ? { surface: court.surface } : {}),
      })),
    [planner, tc],
  );

  /** Proporcjonalna os czasu zbudowana z plannera na wybrany dzien. */
  const timeline: ScheduleTimeline = useMemo(() => {
    if (!planner) return { startMinutes: 0, endMinutes: 0, tiles: [], nowMinutes: null };
    return buildScheduleTimeline(planner, sideText, { todayIso, nowMinutes });
    // sideText zalezy wylacznie od mapy nazwisk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planner, names, todayIso, nowMinutes]);

  if (competitionQuery.isError || matchesQuery.isError) {
    return <Alert tone="danger">{t('center.loadError')}</Alert>;
  }
  if (matchesQuery.isPending) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (matches.length === 0) {
    return <EmptyState icon={<Swords />} title={t('matchesTab.empty')} className="min-h-[16rem]" />;
  }

  /** Etykieta fazy: runda drabinki albo grupa. */
  function phaseOf(match: ApiMatch): string {
    if (match.groupNumber === 0) return t('matchResult.finalGroup');
    if (match.groupNumber !== null) {
      return t('matchResult.group', { number: match.groupNumber });
    }
    return roundLabel(match.round) ?? '—';
  }

  /** Jedna strona meczu: kolka z inicjalami + nazwiska (zwyciezca pogrubiony). */
  function side(ids: readonly string[], winner: boolean) {
    if (ids.length === 0) return <span className="text-fg-subtle">—</span>;
    return (
      <span className="inline-flex items-center gap-1.5">
        {ids.map((id) => (
          <Avatar key={id} name={names.get(id) ?? '…'} size="xs" colorful />
        ))}
        <span className={winner ? 'font-bold text-fg-strong' : 'text-fg'}>{sideText(ids)}</span>
      </span>
    );
  }

  /** Stan startu meczu: „wkrotce" (do godziny), „opozniony" albo neutralny. */
  function startState(match: ApiMatch): 'soon' | 'overdue' | null {
    if (match.status !== 'SCHEDULED' || !match.scheduledDate || !match.scheduledTime) return null;
    if (match.scheduledDate < todayIso) return 'overdue';
    if (match.scheduledDate > todayIso) return null;
    const start = toMinutes(match.scheduledTime);
    if (start < nowMinutes) return 'overdue';
    return start - nowMinutes <= 60 ? 'soon' : null;
  }

  /** Klik w mecz na osi czasu — przejscie do wpisania wyniku. */
  function openMatchResult(matchId: string) {
    const targetCompetitionId = plannerMatches.find((match) => match.id === matchId)?.competitionId;
    if (targetCompetitionId) {
      void navigate(`/turnieje/${targetCompetitionId}/mecze/${matchId}/wynik`);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Kafelki statystyk turnieju */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile
          icon={<CalendarDays />}
          value={scheduledCount}
          label={t('matchesTab.stats.scheduled')}
        />
        <StatTile icon={<Zap />} value={liveCount} label={t('matchesTab.stats.live')} />
        <StatTile
          icon={<CheckCircle2 />}
          value={`${playedCount}/${matches.length}`}
          label={t('matchesTab.stats.played')}
        />
        <StatTile
          icon={<CalendarX />}
          value={unscheduledCount}
          label={t('matchesTab.stats.unscheduled')}
        />
      </div>

      {/* Pasek narzedzi: filtry listy albo wybor dnia osi czasu + przelacznik widoku */}
      <div className="flex flex-wrap items-center gap-2">
        {view === 'list' ? (
          <>
            <Select
              selectSize="sm"
              aria-label={t('matchesTab.filterStatus')}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
              className="w-44"
            >
              {STATUS_FILTERS.map((status) => (
                <option key={status} value={status}>
                  {status === 'ALL' ? t('matchesTab.filterAll') : t(`matchResult.status.${status}`)}
                </option>
              ))}
            </Select>
            <Select
              selectSize="sm"
              aria-label={tc('scheduledMatchesPage.filters.court')}
              className="w-40"
              value={courtFilter}
              onChange={(event) => setCourtFilter(event.target.value)}
            >
              <option value="">{tc('scheduledMatchesPage.filters.allCourts')}</option>
              {(clubCourtsQuery.data ?? []).map((court) => (
                <option key={court.id} value={court.id}>
                  {court.name || tc('scheduledMatchesPage.court', { number: court.number })}
                </option>
              ))}
            </Select>
            <SegmentedControl
              size="sm"
              label={tc('scheduledMatchesPage.filters.range')}
              value={range}
              onValueChange={setRange}
              options={[
                { value: 'all', label: tc('scheduledMatchesPage.filters.rangeAll') },
                { value: 'today', label: tc('scheduledMatchesPage.filters.rangeToday') },
                { value: 'tomorrow', label: tc('scheduledMatchesPage.filters.rangeTomorrow') },
                { value: 'dayAfter', label: tc('scheduledMatchesPage.filters.rangeDayAfter') },
                { value: 'nextWeek', label: tc('scheduledMatchesPage.filters.rangeNextWeek') },
              ]}
            />
          </>
        ) : (
          <>
            <DateInput
              inputSize="sm"
              aria-label={tc('scheduledMatchesPage.grid.day')}
              value={plannerDate}
              onChange={(event) => setPlannerDate(event.target.value)}
            />
            <Button size="sm" variant="secondary" onClick={() => setPlannerDate(todayIso)}>
              {tc('scheduledMatchesPage.filters.rangeToday')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void navigate('/turnieje/infrastruktura')}
            >
              {tc('scheduledMatchesPage.grid.openPlanner')}
            </Button>
          </>
        )}

        <SegmentedControl
          size="sm"
          className="ml-auto"
          label={tc('scheduledMatchesPage.filters.view')}
          value={view}
          onValueChange={setView}
          options={[
            { value: 'list', label: tc('scheduledMatchesPage.filters.viewList'), icon: <List /> },
            {
              value: 'grid',
              label: tc('scheduledMatchesPage.filters.viewGrid'),
              icon: <LayoutGrid />,
            },
          ]}
        />
      </div>

      {/* Widok osi czasu — caly dzien klubu, tylko do odczytu */}
      {view === 'grid' && (
        <>
          {plannerQuery.isError && <Alert tone="danger">{t('center.loadError')}</Alert>}
          {clubId && plannerQuery.isPending && <Skeleton className="h-64 w-full" />}
          {planner &&
            (timeline.tiles.length === 0 ? (
              <EmptyState
                icon={<Swords />}
                title={t('matchesTab.empty')}
                description={tc('scheduledMatchesPage.grid.emptyDay')}
                className="min-h-[16rem]"
              />
            ) : (
              <ScheduleGrid
                courts={gridCourts}
                timeline={timeline}
                onMatchClick={openMatchResult}
              />
            ))}
        </>
      )}

      {/* Widok listy */}
      {view === 'list' &&
        (visible.length === 0 ? (
          <EmptyState
            icon={<Swords />}
            title={t('matchesTab.empty')}
            description={tc('scheduledMatchesPage.filters.noResults')}
            className="min-h-[16rem]"
          />
        ) : (
          <TableContainer>
            <Table className="[&_td]:px-3 [&_th]:px-3">
              <TableHead>
                <TableRow>
                  <TableHeaderCell className="w-14">
                    {t('matchesTab.columns.number')}
                  </TableHeaderCell>
                  <TableHeaderCell className="w-32">
                    {t('matchesTab.columns.phase')}
                  </TableHeaderCell>
                  <TableHeaderCell>{t('matchesTab.columns.players')}</TableHeaderCell>
                  <TableHeaderCell className="w-36">
                    {t('matchesTab.columns.score')}
                  </TableHeaderCell>
                  <TableHeaderCell className="w-32">
                    {t('matchesTab.columns.status')}
                  </TableHeaderCell>
                  <TableHeaderCell className="w-28">
                    {t('matchesTab.columns.court')}
                  </TableHeaderCell>
                  <TableHeaderCell className="w-36">{t('matchesTab.columns.term')}</TableHeaderCell>
                  <TableHeaderCell className="w-24 text-right">
                    {t('matchesTab.columns.actions')}
                  </TableHeaderCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {visible.map((match) => {
                  const court = match.courtId ? courts.get(match.courtId) : undefined;
                  const courtLabel = court
                    ? court.name || t('matchResult.court', { number: court.number })
                    : null;
                  const term =
                    [formatDate(match.scheduledDate), shortTime(match.scheduledTime)]
                      .filter(Boolean)
                      .join(', ') || null;
                  const winner1 = match.result === 'PLAYER1_WIN';
                  const winner2 = match.result === 'PLAYER2_WIN';
                  const state = startState(match);
                  // Dopoki mecz nie ma rozstrzygniecia, kort i termin sa informacja
                  // uzytkowa („gdzie i o ktorej") — potem to juz tylko historia.
                  const pending =
                    match.status !== 'COMPLETED' &&
                    match.status !== 'WALKOVER' &&
                    match.status !== 'CANCELLED';

                  return (
                    <TableRow
                      key={match.id}
                      className="cursor-pointer transition-colors hover:bg-surface-hover"
                      onClick={() =>
                        void navigate(`/turnieje/${competitionId}/mecze/${match.id}/wynik`)
                      }
                    >
                      <TableCell className="text-sm text-fg-subtle tabular-nums">
                        {match.matchNumber ?? '—'}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'text-sm whitespace-nowrap',
                          // Faza pucharowa dostaje akcent; grupy i kolejki zostaja szare.
                          match.groupNumber === null
                            ? (roundAccent(match.round) ?? 'text-fg-muted')
                            : 'text-fg-muted',
                          match.round === 'F' && 'font-semibold',
                        )}
                      >
                        {phaseOf(match)}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center text-sm">
                          {side(match.team1Ids, winner1)}
                          <span className="px-2 text-fg-subtle">vs</span>
                          {side(match.team2Ids, winner2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-fg tabular-nums">
                        {match.status === 'WALKOVER'
                          ? t('matchResult.status.WALKOVER')
                          : (scoreSummary(match) ?? '—')}
                      </TableCell>
                      <TableCell>
                        <Badge tone={STATUS_TONE[match.status]} size="sm">
                          <MatchStatusMark status={toViewStatus(match.status)} size="sm" />
                          {t(`matchResult.status.${match.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {match.courtId && courtLabel ? (
                          <span
                            className={cn(
                              'inline-flex h-6 items-center rounded-full px-2.5 text-xs font-semibold whitespace-nowrap',
                              courtAccent(match.courtId),
                            )}
                          >
                            {courtLabel}
                          </span>
                        ) : (
                          <span className="text-sm text-fg-subtle">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap text-fg-muted tabular-nums">
                        <span className="inline-flex items-center gap-1.5">
                          {term === null ? (
                            '—'
                          ) : pending ? (
                            <span className="rounded-md bg-brand-subtle px-2 py-0.5 font-bold text-brand-text">
                              {term}
                            </span>
                          ) : (
                            term
                          )}
                          {state === 'soon' && (
                            <Badge tone="success" size="sm">
                              <AlarmClock className="size-3" aria-hidden="true" />
                              {tc('scheduledMatchesPage.startingSoon')}
                            </Badge>
                          )}
                          {state === 'overdue' && (
                            <Badge tone="warning" size="sm">
                              {tc('scheduledMatchesPage.overdue')}
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          {/* Szybka zmiana terminu — tylko dla meczow przed startem. */}
                          {match.status === 'SCHEDULED' && clubId && (
                            <IconButton
                              size="sm"
                              variant="secondary"
                              icon={<Pencil />}
                              label={tc('scheduledMatchesPage.actions.edit')}
                              onClick={(event) => {
                                event.stopPropagation();
                                setEditing(match);
                              }}
                            />
                          )}
                          {/* Start tylko dla skompletowanych par — slot bez przeciwnika
                              i tak odbiłby się od walidacji backendu. */}
                          {match.status === 'SCHEDULED' &&
                            match.team1Ids.length > 0 &&
                            match.team2Ids.length > 0 && (
                              <IconButton
                                size="sm"
                                variant="secondary"
                                icon={<Play />}
                                label={t('matchResult.actions.start')}
                                onClick={(event) => {
                                  // Wiersz nawiguje do wyniku — start nie ma tam prowadzić.
                                  event.stopPropagation();
                                  actionMutation.mutate({ matchId: match.id, action: 'start' });
                                }}
                              />
                            )}
                          {/* Koniec gry bez wyniku — mecz przechodzi w „Zakończony
                              (brak wyniku)" i czeka na rezultat. */}
                          {(match.status === 'IN_PROGRESS' || match.status === 'SUSPENDED') && (
                            <IconButton
                              size="sm"
                              variant="secondary"
                              icon={<Flag />}
                              label={t('matchResult.actions.finish')}
                              onClick={(event) => {
                                event.stopPropagation();
                                actionMutation.mutate({ matchId: match.id, action: 'finish' });
                              }}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <TableFooter>
              {t('matchesTab.footer', { shown: visible.length, total: matches.length })}
            </TableFooter>
          </TableContainer>
        ))}

      {editing && (
        <ScheduleMatchDialog
          key={editing.id}
          open
          onClose={() => setEditing(null)}
          match={editing}
          matchLabel={`${sideText(editing.team1Ids)} vs ${sideText(editing.team2Ids)}`}
          courts={clubCourtsQuery.data ?? []}
        />
      )}
    </div>
  );
}
